import React, { useState } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  useTheme,
  useMediaQuery,
  makeStyles,
  Divider,
  Button,
  Hidden,
} from "@material-ui/core"
import Config from "react-global-configuration"
import { useHistory, useParams } from "react-router-dom"
import { DialogTitleCloser } from "../Creators/DialogTitleCloser"
import { InlineTitleCloser } from "../Creators"
import { InlineTitle, LoadingSpinner } from ".."
import { ProcessSteps } from "./ProcessSteps"
import { TemplateCategoryChip } from "../Chips"
import { ModuleHeading } from "../Headings"
import { InfoSection } from "../Viewers"
import { PaperBox } from "../Boxes"
import { FormatDate } from "../Format"
import { Icon } from "../Icon"
import { useMutationCopyProcess, useMutationUseLibraryTemplate } from "../../data"
import { useAuth } from "../../services"
import { toId, useProcessUtils } from "../../utils"
import { Author, AuthorTools } from "../Author"
import { AssigneeDetailer } from "../Assignees"

const useStyles = makeStyles((theme) => ({
  detail: {
    backgroundColor: theme.palette.incomplete.background,
    border: `solid 2px ${theme.palette.incomplete.background}`,
    borderRadius: "8px",
  },
  detailInner: {
    padding: "20px 24px",
  },
  detailName: {
    fontSize: 26,
    lineHeight: "36px",
    marginBottom: theme.spacing(1),
  },
  detailDescription: {
    fontSize: 16,
    lineHeight: "20px",
    marginBottom: theme.spacing(2),
  },
  detailSteps: {
    display: "flex",
    flexDirection: "row",
    fontSize: 16,
  },
  infoLight: {
    color: theme.palette.text.light,
  },
}))

const TemplateViewer = ({ template, onClose, onUsed, onDisable, onCategoryClick }) => {
  const history = useHistory()
  const { set, category } = useParams()
  const classes = useStyles()
  const theme = useTheme()
  const isInline = useMediaQuery(theme.breakpoints.down("xs"))
  const [functionsLoading, setFunctionsLoading] = useState(false)
  const [copyTemplate, { loading: useLibraryTemplateLoading }] = useMutationUseLibraryTemplate()
  const [copyProcess, { loading: copyProcessLoading }] = useMutationCopyProcess()
  const { getNavigateToTemplateLink } = useProcessUtils()
  const {
    settings: { locations: userLocations },
    hasPermission,
  } = useAuth()
  const { featuredCategory: featuredId } = Config.get("templates")
  const inactive = template && template.inactive

  const handleOnClose = () => {
    onClose(true)
  }
  const handleCategoryClick = (id) => {
    if (onCategoryClick) onCategoryClick(id)
  }
  const handleEditTemplate = () => {
    history.push(getNavigateToTemplateLink(template, set || "library", category, "edit"))
  }
  const handleUseTemplate = async () => {
    let item
    if (libraryTemplate()) {
      // copy from template library
      item = await copyTemplate({ variables: { id: template.id } })
      item = item.data ? item.data.useLibraryTemplate : null
    } else {
      // copy own process
      item = await copyProcess({ variables: { id: template.id } })
      item = item.data ? item.data.copyProcess : null
    }
    if (onUsed && item) onUsed(item)
  }
  const handleDisable = async () => {
    if (onDisable) {
      setFunctionsLoading(true)
      await onDisable(template, true)
      setFunctionsLoading(false)
    }
  }

  const libraryTemplate = () => template.categories && template.categories.length > 0

  if (!template) {
    return null
  }

  const isLibraryTemplate = libraryTemplate()

  const loading = useLibraryTemplateLoading || copyProcessLoading || functionsLoading

  const locations =
    template.locations &&
    template.locations.map((templateLocation) => {
      const item = userLocations.find((ul) => toId(ul) === toId(templateLocation))
      if (item) return item
      return { id: toId(templateLocation), name: "(Hidden)", noAccess: true }
    })

  const display = (
    <>
      <Box mt={isInline ? 1 : 0} px={isInline ? 2 : 0}>
        {template && template.__typename === "Task" && (
          <>
            <Box className={classes.detail} mb={2}>
              <Box className={classes.detailInner}>
                <ModuleHeading className={classes.detailName} noMargin>
                  {template.name}
                </ModuleHeading>
                {template.description && <Box className={classes.detailDescription}>{template.description}</Box>}
              </Box>
            </Box>

            <PaperBox mb={3}>
              <InfoSection variant="info">
                <Box display="flex" flexDirection="row" flexGrow={1} alignItems="center">
                  <Author author={template.author} prefix="Created by" className={classes.infoLight} />
                  {set === "active-tasks" && (
                    <Box ml="auto" display="flex" flexDirection="row">
                      <Box alignSelf="center">
                        <AuthorTools
                          item={template}
                          subject="task"
                          admins={{ edit: "task_update_all", disable: "task_update_all" }}
                          disableLabel={inactive ? "Make active" : "Make inactive"}
                          onCopy={handleUseTemplate}
                          onDisable={handleDisable}
                          p={0.5}
                          disabled={loading}
                        />
                      </Box>
                      {hasPermission("task_update_all") && (
                        <Box ml={1.5}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleEditTemplate}
                            disabled={loading}
                            title="Edit"
                          >
                            Edit
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </InfoSection>
              <Divider />
              <InfoSection title="Date added" value={<FormatDate value={template.updatedAt} />} />
              <Divider />
              <InfoSection title="Scheduled" value={<FormatDate value={template.after} />} />
              {locations && <Divider />}
              {locations && <InfoSection title="Locations" value={locations.map((item) => item.name).join(", ")} />}
            </PaperBox>

            <AssigneeDetailer users={template.users} groups={template.groups} />
          </>
        )}
        {template?.__typename === "Process" && (
          <>
            <Box className={classes.detail} mb={2}>
              <Box className={classes.detailInner}>
                <ModuleHeading className={classes.detailName} noMargin={!isLibraryTemplate}>
                  {template.name}
                </ModuleHeading>
                {template.description && <Box className={classes.detailDescription}>{template.description}</Box>}
                {template.categories && (
                  <Box display="flex" flexDirection="row">
                    {[...template.categories]
                      .sort((a, b) => {
                        if (a.id === featuredId) return -1
                        if (b.id === featuredId) return 1
                        return a.name.localeCompare(b.name)
                      })
                      .map((item) => (
                        <Box key={item.id} mr={0.5}>
                          <TemplateCategoryChip category={item} onClick={handleCategoryClick} />
                        </Box>
                      ))}
                  </Box>
                )}
              </Box>
            </Box>

            <PaperBox mb={3}>
              <InfoSection variant="info">
                <Box py={isLibraryTemplate ? 1 : 0} display="flex" flexDirection="row" flexGrow={1} alignItems="center">
                  {isLibraryTemplate && <Icon name="operandio" size={32} />}
                  {isLibraryTemplate && (
                    <Box className={classes.infoLight} ml={1.5}>
                      Created by Operandio
                    </Box>
                  )}
                  {!isLibraryTemplate && (
                    <Author author={template.author} prefix="Created by" className={classes.infoLight} />
                  )}
                  <Box ml="auto" display="flex" flexDirection="row">
                    {isLibraryTemplate && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUseTemplate}
                        disabled={loading}
                        title="Make a copy"
                        data-cy="Button-copy"
                      >
                        {!loading && (
                          <>
                            <Hidden xsDown>Make a copy</Hidden>
                            <Hidden smUp>
                              <Icon name="copy" />
                            </Hidden>
                          </>
                        )}
                        {loading && <LoadingSpinner size="24px" delay={false} />}
                      </Button>
                    )}
                    {!isLibraryTemplate && (
                      <>
                        <Box alignSelf="center">
                          <AuthorTools
                            item={template}
                            subject="process"
                            admins={{ edit: "process_update_all", disable: "process_update_all" }}
                            disableLabel={inactive ? "Make active" : "Make inactive"}
                            onCopy={handleUseTemplate}
                            onDisable={handleDisable}
                            p={0.5}
                          />
                        </Box>
                        {hasPermission("process_update_all") && (
                          <Box ml={1.5}>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handleEditTemplate}
                              disabled={loading}
                              title="Edit"
                            >
                              Edit
                            </Button>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              </InfoSection>
              <Divider />
              <InfoSection title="Date added" value={<FormatDate value={template.updatedAt} />} />
              <Divider />
              <InfoSection title="Steps" value={`${template.steps.length} steps`} />
              {locations && <Divider />}
              {locations && <InfoSection title="Locations" value={locations.map((item) => item.name).join(", ")} />}
            </PaperBox>

            <ProcessSteps steps={template.steps} isPreview scrollableTarget="TemplateViewer-content" />
          </>
        )}
      </Box>
    </>
  )

  if (isInline) {
    return (
      <>
        <InlineTitle>
          Template details
          <InlineTitleCloser onClose={handleOnClose} />
        </InlineTitle>
        {display}
      </>
    )
  }
  return (
    <Dialog
      open={template !== null}
      onClose={handleOnClose}
      aria-labelledby="form-dialog-title"
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="form-dialog-title">
        Template details
        <DialogTitleCloser onClose={handleOnClose} />
      </DialogTitle>
      <DialogContent id="TemplateViewer-content">{display}</DialogContent>
    </Dialog>
  )
}

export { TemplateViewer }
